import type { ButtonTypes } from "./UiSettings";
import type { GeometryExportFormatKey } from "@egr/xbox/utils/FileFormats";
import type { Palette } from '@mui/material/styles';

import { ArticleNoInSubInfo, ManufacturerInSubInfo, SeriesInSubInfo } from "../DefaultValues";

import { getEnvBoolean, getEnvVar } from "@egr/xbox/utils/ReactScriptHelper";

type OciVersion = '4' | '5';
type AllowedBackgroundColors = 'radiantgrey' | 'black' | 'white' | 'transparent';

export enum UiThemeType {
    DEFAULT = 'DEFAULT',
    VITRA = 'VITRA',
}

export const enum PropertyEditorVariant {
    /** default value */
    FULL = 'full',
    COMPACT = 'compact',
}

export const enum ArticleDrawerVariant {
    /** default value */
    SINGLE = 'single',
    MODULAR = 'modular',
}

export const DefaultUiConfig = {
    active: false,
    gatekeeperId: null as string | null,
    permissions: {
        pricing: false,
        allowMultipleManufacturerPEC: true,
        iframeAllowedParents: [] as Array<string>,
        ociExport: false,
        postMessageContactForm: false,
        showUrlsInCatalog: true,
        cadFormats: [] as Array<GeometryExportFormatKey>
    },
    settings: {
        general: {
            lang: 'en',
            ofmlLang: undefined as string | undefined,
            lengthUnit: 'Meter',
            pim: getEnvBoolean('PIM', false),
            pimJSON: getEnvBoolean('PIMJSON', false),
            catalogRootAllowed: true,
            userTracking: false,
            customParentTracking: true,
            enableIframeResizer: false,
            fullscreenOnly: false,
            ociVersion: '4' as OciVersion,
            generateContactRequestPdf: false,
            propertyServiceUrl: undefined as string | undefined,
            propertyDefinitionUrl: undefined as string | undefined,
            translationsImportUrl: undefined as string | undefined,
            migrateArticles: false,
            floor: undefined as { color: string; grid: boolean; size: number; } | undefined,
        },
        pricing: {
            currency: 'EUR',
            calculationScheme: 'STDB2B_FACTS',
            currencyLeft: getEnvBoolean('CURRENCY_LEFT', false),
            tax: 'DE',
            showPrice: true,
            netPrice: true,
            priceServiceUrl: undefined as string | undefined
        },
        report: {
            templateName: 'pCon_facts_ProductSheet',
            enablePricing: false,
            enforcePricing: false,
            footerText: [] as Array<string>,
            logoUrl: '',
            showAdditionalText: true,
            showLongText: true,
            showSpecialModelInfo: true,
            showVariantText: true,
        },
        camera: {
            disableControls: false,
            restrictRotationToZAxis: false,
            lockToTarget: false,
            autorotate: false,
            autorotationDelay: 5000,
            autorotationSpeed: 15,
            autorotationPlayButton: true,
            alpha: undefined as number | undefined,
            beta: undefined as number | undefined,
            fov: undefined as number | undefined,
            zoomToFitOptions: undefined as { margin?: number, minMarginPx?: number, marginH?: number, minMarginPxH?: number } | undefined,
        }
    },
    visibility: {
        general: {
            header: true,
            showQuantityOnUpdate: true
        },
        articleData: {
            description: true,
            manufacturerInSubInfo: ManufacturerInSubInfo,
            seriesInSubInfo: SeriesInSubInfo,
            articleNoInSubInfo: ArticleNoInSubInfo,
            showUneditableProperties: false,
            enableMetatypeProperties: true,
            showArticleName: true,
            showPropertyHeaders: true,
        },
        buttons: {
            '3D': [] as Array<ButtonTypes>,
            '3DPopover': [] as Array<ButtonTypes>,
            footer: [] as Array<ButtonTypes>,
            footerPopover: [] as Array<ButtonTypes>,
            fullscreen: [] as Array<ButtonTypes>,
            fullscreenPopover: [] as Array<ButtonTypes>,
        },
    },
    defaults: {
        lang: 'en',
        showPrice: true,
        netPrice: true,
        header: true,
        fullscreenOnly: false,
        restrictRotationToZAxis: false,
        disableCameraControls: false,
        lockToTarget: false,
        autorotate: false,
        description: true,
        manufacturerInSubInfo: ManufacturerInSubInfo,
        seriesInSubInfo: SeriesInSubInfo,
        articleNoInSubInfo: ArticleNoInSubInfo,
        showArticleName: true,
        showPropertyHeaders: true,
        showUneditableProperties: false,
        enableMetatypeProperties: true
    },
    theme: UiThemeType.DEFAULT,
    styles: {
        mainPrimaryColor: undefined as string | undefined,
        background: 'white' as AllowedBackgroundColors,
        iframeHeightSmall: getEnvVar('IFRAME_HEIGHT_SMALL', '166%'),
        buttonText: undefined as string | undefined, // overrides label of "Send Request" and "Add to cart" buttons
        paletteImport: getEnvVar('PALETTE_IMPORT'),
        cssImport: getEnvVar('CSS_IMPORT'),
        articleDrawerVariant: ArticleDrawerVariant.SINGLE,
        propertyEditorVariant: PropertyEditorVariant.FULL,
        paletteOverride: {} as Partial<Palette> // will be set after optional JSON was loaded
    }
};

export type UiConfig = typeof DefaultUiConfig;
export type UiPermissions = UiConfig['permissions'];
export type UiSettings = UiConfig['settings'];
export type UiVisibility = UiConfig['visibility'];
export type UiCameraSettings = UiConfig['settings']['camera'];
export type UiStyles = UiConfig['styles'];

type RawUiPermissions = Omit<UiConfig['permissions'], 'iframeAllowedParents' | 'cadFormats'> & {
    iframeAllowedParents: Array<string> | null;
    cadFormats: Array<GeometryExportFormatKey> | undefined;
};

type RawReportSettings = Omit<UiConfig['settings']['report'], 'footerText'> & {
    footerText: string | Array<string>;
};

type RawUiStyles = Omit<UiConfig['styles'], 'paletteOverride'> & {
    paletteOverride: Partial<Palette> | undefined;
};

export type RawUiConfig = Omit<UiConfig, 'permissions' | 'settings' | 'styles'> & {
    permissions: RawUiPermissions;
    settings: Omit<UiConfig['settings'], 'report'> & {
        report: RawReportSettings;
    };
    styles: RawUiStyles;
};

export function normalizeUiConfig(rawConfig: RawUiConfig): UiConfig {
    return {
        ...rawConfig,
        permissions: {
            ...rawConfig.permissions,
            iframeAllowedParents: rawConfig.permissions.iframeAllowedParents ?? [],
            cadFormats: rawConfig.permissions.cadFormats ?? [],
        },
        settings: {
            ...rawConfig.settings,
            report: {
                ...rawConfig.settings.report,
                footerText: rawConfig.settings.report.footerText == null
                    ? []
                    : Array.isArray(rawConfig.settings.report.footerText)
                        ? rawConfig.settings.report.footerText
                        : [rawConfig.settings.report.footerText],
            },
        },
        styles: {
            ...rawConfig.styles,
            paletteOverride: rawConfig.styles.paletteOverride ?? {},
        },
    };
}
